
import { color } from "framer-motion";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import numWords from "num-words";

pdfMake.vfs = pdfFonts.vfs;

// Base64 convert function
const toBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (err) => reject(err);
  });
};

const formatAmount = (num) => {
  if (num == null || isNaN(num)) return "0.00";
  return Number(num).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function formatOnlyDate(dateTimeString) {
  if (!dateTimeString) return "N/A";
  const datePart = dateTimeString.split("T")[0];
  const [year, month, day] = datePart.split("-");
  return `${day}-${month}-${year}`;
}

// 🔹 Line count helpers for vertical centering
const countLines = (text) => {
  if (!text) return 1;
  return text.split("\n").length;
};

const estimateDescriptionLines = (itemName, description) => {
  let lines = 1; // for item name
  if (description && description.trim()) {
    const descLength = description.length;
    lines += Math.ceil(descLength / 60); // ~60 chars per line for description width
  }
  return Math.max(lines, 2);
};

const vCenterByLines = (text, totalLines, align = "center", fontSize = 8) => {
  const lineHeight = fontSize * 1.2;
  const contentHeight = lineHeight;
  const totalHeight = totalLines * lineHeight;
  const marginTop = Math.max(0, (totalHeight - contentHeight) / 2);

  return {
    text,
    alignment: align,
    fontSize: fontSize,
    margin: [0, marginTop, 0, marginTop],
  };
};

// Main print function
export const printPerforma = async (invoice, logoPath = "/logoblack.png") => {
  if (!invoice) {
    console.error("Invoice is null or undefined");
    return;
  }

  try {
    let logoBase64 = null;
    try {
      logoBase64 = await toBase64(logoPath);
    } catch (logoError) {
      console.warn("Logo conversion failed, continuing without logo:", logoError);
    }

    // ✅ GST Related Rows - NULL SAFE
    const gstRelatedRows =
      Number(invoice.gst_amount || 0) > 0
        ? [
          [
            { text: "", colSpan: 4 },
            "",
            "",
            "",
            {
              text: `ADD: ${invoice.gst_type || ""} @${invoice.gst_percent || 0}%`,
              bold: true,
            },
            {
              text: formatAmount(invoice.gst_amount),
              alignment: "right",
            },
          ],
          [
            { text: "", colSpan: 4 },
            "",
            "",
            "",
            { text: "TOTAL WITH GST", bold: true },
            {
              text: formatAmount(
                Number(invoice.basic_value || 0) +
                Number(invoice.gst_amount || 0)
              ),
              alignment: "right",
            },
          ],
          [
            { text: invoice.advance_narration, colSpan: 4 },
            "",
            "",
            "",
            { text: "LESS ADVANCE", bold: true },
            {
              text: formatAmount(invoice.qty_advance_amt || 0),
              alignment: "right",
            },
          ],
        ]
        : [];

    // ✅ Retention/Narration Row - NULL SAFE
    const stored = localStorage.getItem("selectedDivision");
    const division = JSON.parse(stored);
    const retentionRow = [];
    if (
      (invoice.retention_narration && invoice.retention_narration.trim() !== "") ||
      (invoice.retention_amount && Number(invoice.retention_amount) > 0) ||
      (invoice.retention_percentage && Number(invoice.retention_percentage) > 0)
    ) {
      retentionRow.push([
        {
          text: invoice.retention_narration || `LESS: ${invoice.retention_percentage || 0}% Retention`,
          bold: true,
          colSpan: 5,
          alignment: "left",
        },
        "",
        "",
        "",
        "",
        {
          text: formatAmount(invoice.retention_amount || 0),
          alignment: "right",
        },
      ]);
    }

    // ✅ Items list - NULL SAFE
    const itemsList = invoice.showEditColumns
      ? (Array.isArray(invoice.pi_main_id) ? invoice.pi_main_id : [])
      : (Array.isArray(invoice.project?.items) ? invoice.project.items : []);

    // ✅ Tax Terms - NULL SAFE
    const taxTermsStack = Array.isArray(invoice.tax_terms) && invoice.tax_terms.length > 0
      ? invoice.tax_terms.map((t, i) => ({
        text: `${i + 1}) ${t?.term || ""}`,
        margin: [0, 2, 0, 2],
      }))
      : [{ text: "-", margin: [0, 2, 0, 2] }];

    // ✅ Payment Terms - NULL SAFE
    const paymentTermsStack = Array.isArray(invoice.payment_terms) && invoice.payment_terms.length > 0
      ? invoice.payment_terms.map((t, i) => ({
        text: `${i + 1}) ${t?.term || ""}`,
        margin: [0, 2, 0, 2],
      }))
      : [{ text: "-", margin: [0, 2, 0, 2] }];

    // ✅ Items Rows with Vertical Centering - Filter items where qty > 0
    const itemRows = (Array.isArray(itemsList) ? itemsList : [])
      .filter((item) => {
        const qty = invoice.showEditColumns
          ? Number(item?.pi_qty || 0)
          : Number(item?.quantity || 0);
        return qty > 0;
      })
      .map((item, index) => {
        const itemName = item?.item_name || "";
        const description = item?.description || "";
        const descLines = estimateDescriptionLines(itemName, description);

        return [
          vCenterByLines(index + 1, descLines, "center", 8),
          {
            stack: [
              { text: itemName, bold: true, fontSize: 8 },
              { text: description, fontSize: 7, italics: true },
            ],
            margin: [0, 2, 0, 2],
          },
          vCenterByLines(item?.uom || "", descLines, "center", 8),
          vCenterByLines(
            invoice.showEditColumns ? (item?.pi_qty || 0) : (item?.quantity || 0),
            descLines,
            "center",
            8
          ),
          vCenterByLines(formatAmount(item?.rate), descLines, "right", 8),
          vCenterByLines(
            invoice.showEditColumns
              ? formatAmount(item?.total_amount)
              : formatAmount(item?.amount),
            descLines,
            "right",
            8
          ),
        ];
      });

    // Helper function to convert to Camel Case
    const toCamelCase = (str) => {
      return str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    const amountInWords = invoice.advance_received
      ? toCamelCase(numWords(Math.floor(Number(invoice.advance_received))))
      : "Zero";

    const docDefinition = {
      pageSize: "A4",
      pageMargins: [40, 40, 40, 40],
      defaultStyle: {
        fontSize: 8,
      },
      content: [
        // Heading
        {
          table: {
            widths: ["100%"],
            body: [
              [
                {
                  text: invoice.ra_bill_number
                    ? `PROFORMA INVOICE / ${invoice.ra_bill_number}`
                    : "PROFORMA INVOICE",
                  alignment: "center",
                  bold: true,
                  fontSize: 16,
                  margin: [0, 5, 0, 5],
                  color: "red",
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },
        // Seller, No & Date, Buyer's Order
        {
          table: {
            widths: ["50%", "25%", "25%"],
            body: [
              [
                { text: "SELLER", bold: true, alignment: "center" },
                { text: "No & Date", bold: true, alignment: "center" },
                {
                  text: "Buyer's Order No & Date",
                  bold: true,
                  alignment: "center",
                },
              ],
              [
                {
                  columns: [
                    logoBase64
                      ? {
                        image: logoBase64,
                        width: 100,
                        alignment: "left",
                        margin: [0, 15, 0, 0],
                      }
                      : { text: "" },
                    {
                      stack: [
                        {
                          text: division?.company_name || "CRANOIST MHE PVT. LTD.",
                          bold: true,
                          alignment: "center",
                        },
                        {
                          text: division?.address || "Works: Plot No.37, Phase-II, Siltara Ind. Area,",
                          alignment: "center",
                        },
                        // {
                        //   text: "Raipur (C.G.), Pin: 493111",
                        //   alignment: "center",
                        // },
                        {
                          text: "Contact: 09302125991, 09302122967",
                          alignment: "center",
                        },
                        {
                          text: "E-mail: kp_grandsons@rediffmail.com",
                          alignment: "center",
                          color: "blue",
                        },
                      ],
                      width: "*",
                    },
                  ],
                },
                {
                  text:
                    `PI No: ${invoice.pi_no || "N/A"}\n\n` +
                    `PI Date: ${invoice.pi_date ? formatOnlyDate(invoice.pi_date) : "N/A"}\n\n` +
                    `Created Date: ${invoice.created_at ? formatOnlyDate(invoice.created_at) : "N/A"}\n\n` +
                    `S.O. No: ${invoice.project?.so_no || "N/A"}`,
                  alignment: "center",
                },
                {
                  text:
                    `P.O. No: ${invoice.project?.po_number || "N/A"}\n\n` +
                    `P.O. Date: ${invoice.project?.order_date
                      ? new Date(invoice.project?.order_date + "T00:00:00").toLocaleDateString("en-GB")
                      : ""
                    }`,
                  alignment: "center",
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },

        // Buyer & Billing Address
        {
          table: {
            widths: ["50%", "50%"],
            body: [
              [
                { text: "BUYER", bold: true, alignment: "center" },
                { text: "BILLING ADDRESS", bold: true, alignment: "center" },
              ],
              [
                {
                  stack: [
                    {
                      text: invoice.project?.party_details?.company_name || "N/A",
                      margin: [0, 2, 0, 2],
                    },
                    {
                      text: invoice.project?.party_details?.address || "",
                      margin: [0, 2, 0, 2],
                    },
                    {
                      text: "GSTIN: " + (invoice.project?.party_details?.gst || "N/A"),
                      margin: [0, 2, 0, 2],
                    },
                    { text: "STATE CODE: 22", margin: [0, 2, 0, 2] },
                  ],
                },
                {
                  stack: [
                    {
                      text: invoice.project?.party_details?.company_name || "N/A",
                      margin: [0, 2, 0, 2],
                    },
                    {
                      text: invoice.address_selected
                        ? invoice.shipping_address
                        : invoice.project?.party_details?.address || "N/A",
                      margin: [0, 2, 0, 2],
                    },
                    {
                      text: "GSTIN: " + (invoice.project?.party_details?.gst || "N/A"),
                      margin: [0, 2, 0, 2],
                    },
                    { text: "STATE CODE: 22", margin: [0, 2, 0, 2] },
                  ],
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },

        // Tax & Payment Terms
        {
          table: {
            widths: ["50%", "50%"],
            body: [
              [
                { text: "TAX TERMS", bold: true, alignment: "center" },
                { text: "PAYMENT TERMS", bold: true, alignment: "center" },
              ],
              [
                {
                  stack: taxTermsStack,
                },
                {
                  stack: paymentTermsStack,
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },

        // ✅ Items Table with Vertical Centering
        {
          table: {
            widths: ["5%", "40%", "10%", "15%", "15%", "15%"],
            body: [
              // Header Row
              [
                { text: "NO", bold: true, alignment: "center" },
                { text: "DESCRIPTION", bold: true, alignment: "center" },
                { text: "UOM", bold: true, alignment: "center" },
                { text: "QTY.", bold: true, alignment: "center" },
                {
                  text: "RATE after disc. (Rs.)",
                  bold: true,
                  alignment: "right",
                },
                { text: "AMOUNT (Rs.)", bold: true, alignment: "right" },
              ],

              // ✅ Data Rows with Vertical Centering
              ...itemRows,

              // BASIC VALUE Row
              [
                { text: "", colSpan: 4 },
                "",
                "",
                "",
                { text: "BASIC VALUE", bold: true },
                {
                  text: formatAmount(invoice.basic_value || 0),
                  alignment: "right",
                },
              ],

              // ✅ GST Related Rows
              ...(Array.isArray(gstRelatedRows) ? gstRelatedRows : []),

              // ✅ Retention/Narration Row
              ...(Array.isArray(retentionRow) ? retentionRow : []),

              // Installment Summary Row
              [
                {
                  text: `${invoice.installment_summary || ""}`,
                  bold: true,
                  colSpan: 5,
                  alignment: "left",
                },
                "",
                "",
                "",
                "",
                {
                  text: formatAmount(invoice.advance_received || 0),
                  alignment: "right",
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },

        // Amount in Words + Footer
        {
          table: {
            widths: ["100%"],
            body: [
              [
                {
                  text: `In Words: Rs. ${amountInWords} Only`,
                  bold: true,
                },
              ],
              [
                {
                  text: "1. Dispute, if any is subject to Raipur Jurisdiction only.\n2. Interest @22% per annum will be charged after 7 days of the presentation of bill.",
                },
              ],
              [
                {
                  table: {
                    widths: ["30%", "70%"],
                    body: [
                      [
                        {
                          text: "Details for Bank Transfer:-",
                          bold: true,
                          decoration: "underline",
                          margin: [0, 0, 0, 2],
                        },
                        {
                          text: "Transfer the payment through (NEFT/RTGS) in below account:",
                          margin: [0, 0, 0, 2],
                        },
                      ],
                      [
                        { text: "A/C NO.:", bold: true },
                        invoice.bank_details?.account_number || "N/A",
                      ],
                      [
                        { text: "BANK NAME:", bold: true },
                        `${invoice.bank_details?.bank_name || ""}, BRANCH - ${invoice.bank_details?.branch_name || ""
                        }`,
                      ],
                      [
                        { text: "IFSC CODE:", bold: true },
                        invoice.bank_details?.ifsc_code || "N/A",
                      ],
                      [
                        { text: "MICR CODE:", bold: true },
                        invoice.bank_details?.micr_code || "N/A",
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                    paddingTop: () => 2,
                    paddingBottom: () => 2,
                  },
                },
              ],
              [
                {
                  table: {
                    widths: ["50%", "50%"],
                    body: [
                      [
                        {
                          text: "OUR GST INFORMATION",
                          bold: true,
                          alignment: "center",
                          margin: [0, 4, 0, 4],
                        },
                        {
                          text: "AUTHORIZATION",
                          bold: true,
                          alignment: "center",
                          margin: [0, 4, 0, 4],
                        },
                      ],
                      [
                        {
                          stack: [
                            {
                              text: `GSTIN: ${invoice.order?.gst_no || division?.gst_no || "22AAHFK2314C1Z4"
                                }`,
                              margin: [20, 2, 0, 2],
                            },
                            {
                              text: `PAN NO.: ${invoice.order?.pan_no || division?.pan_no || "AAHFK2314C1"
                                }`,
                              margin: [20, 2, 0, 2],
                            },
                          ],
                        },
                        {
                          text: "For KP AND GRANDSONS PVT. LTD.\n(Signature)\nAuth. Signatory",
                          alignment: "center",
                          margin: [5, 2, 0, 2],
                        },
                      ],
                    ],
                  },
                  layout: {
                    hLineWidth: () => 0.5,
                    vLineWidth: () => 0.5,
                    hLineColor: () => "black",
                    vLineColor: () => "black",
                  },
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            hLineColor: () => "black",
            vLineColor: () => "black",
          },
        },
      ],
    };

    pdfMake.createPdf(docDefinition).open();
  } catch (error) {
    console.error("PDF Generation failed:", error);
  }
};